import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AuthCallback — handles BOTH OAuth return paths:
 *
 *   A. `/auth/google?code=...&state=...` — direct Google OAuth (Jun 2026).
 *      We validate the state against sessionStorage, POST the code to
 *      `/api/auth/google/callback`, and on success the backend sets the
 *      session cookie + returns the user. We then route to the saved
 *      `next` path (default /dashboard).
 *
 *   B. `#session_id=...` in the URL hash — legacy Emergent Auth flow
 *      (kept temporarily while we migrate; remove once production no
 *      longer references auth.emergentagent.com).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH.
 */

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const isGoogleFlow = location.pathname === '/auth/google';

    const failToLogin = (msg) => {
      toast.error(msg, { duration: 10000 });
      // Send the user back to the new login page with the error preserved.
      navigate('/login', { state: { error: msg }, replace: true });
    };

    const failToHome = (msg) => {
      toast.error(msg, { duration: 10000 });
      navigate('/', { replace: true });
    };

    const processGoogle = async () => {
      const search = new URLSearchParams(location.search);
      const code = search.get('code');
      const state = search.get('state');
      const oauthError = search.get('error');

      if (oauthError) {
        const msg =
          oauthError === 'access_denied'
            ? 'Sign-in was cancelled. Tap "Sign in with Google" again whenever you\'re ready.'
            : `Google sign-in failed (${oauthError}). Please try again.`;
        failToLogin(msg);
        return;
      }

      if (!code) {
        failToLogin('No sign-in code returned by Google. Please try again.');
        return;
      }

      // CSRF state check
      let expectedState = null;
      try { expectedState = sessionStorage.getItem('colosseum_oauth_state'); } catch { /* ignore */ }
      if (expectedState && state && expectedState !== state) {
        failToLogin('Sign-in security check failed. Please try again.');
        return;
      }
      try { sessionStorage.removeItem('colosseum_oauth_state'); } catch { /* ignore */ }

      try {
        const redirectUri = window.location.origin + '/auth/google';
        const response = await axios.post(
          `${API}/auth/google/callback`,
          { code, redirect_uri: redirectUri, state },
          { withCredentials: true }
        );

        // Persist the session token to localStorage so cross-origin API
        // calls that hit Cloudflare's CORS layer can authenticate via the
        // `Authorization: Bearer <token>` header (the ingress overrides
        // `Access-Control-Allow-Origin` to `*`, which would invalidate
        // credentialed cookie requests). The httpOnly cookie still works
        // for same-origin calls; the Bearer header is the cross-origin
        // safety net. Owen's Jun 2026 "Failed to fetch" bug on journal
        // PDF downloads traced to exactly this CORS+credentials clash.
        //
        // Owen Feb 28, 2026 round 55 — ALSO hand the freshly returned
        // user object straight to AuthContext via signIn(). That way the
        // very next route mount inside the app sees status='authenticated'
        // synchronously, no /auth/me round trip required.
        signIn(response.data?.user || null, response.data?.session_token || null);

        // Where to land — saved by Login.js before redirect.
        let nextPath = '/dashboard';
        try {
          const saved = sessionStorage.getItem('colosseum_post_login');
          if (saved) nextPath = saved;
          sessionStorage.removeItem('colosseum_post_login');
        } catch { /* ignore */ }

        // Founder-invite override (same logic as Emergent flow).
        try {
          const inviteToken = localStorage.getItem('colosseum_invite_token');
          if (inviteToken) {
            const seen = localStorage.getItem(`colosseum_welcome_seen_${inviteToken}`);
            if (!seen) nextPath = `/founder-welcome?token=${encodeURIComponent(inviteToken)}`;
          }
        } catch { /* ignore */ }

        navigate(nextPath, { state: { user: response.data.user }, replace: true });
      } catch (error) {
        const detail = error?.response?.data?.detail;
        const status = error?.response?.status;
        const msg =
          (typeof detail === 'string' && detail) ||
          (status === 401 ? 'That sign-in link has already been used. Please try again.' : null) ||
          'Google sign-in failed. Please try again.';
        console.error('Google auth callback error:', error, 'detail:', detail, 'status:', status);
        failToLogin(msg);
      }
    };

    const processEmergent = async () => {
      // Legacy Emergent Auth path — kept until production redeploys with the new flow.
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');
      if (!sessionId) {
        failToHome('Invalid authentication response');
        return;
      }
      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
        // Same Bearer-token persistence as the Google flow above — keeps
        // cross-origin downloads working when Cloudflare overrides CORS.
        // Owen Feb 28, 2026 round 55 — also push the user straight into
        // AuthContext so route transitions don't need another /auth/me.
        signIn(response?.data?.user || null, response?.data?.session_token || null);
        let nextPath = '/';
        try {
          const inviteToken = localStorage.getItem('colosseum_invite_token');
          if (inviteToken) {
            const seen = localStorage.getItem(`colosseum_welcome_seen_${inviteToken}`);
            if (!seen) nextPath = `/founder-welcome?token=${encodeURIComponent(inviteToken)}`;
          }
        } catch { /* ignore */ }
        navigate(nextPath, { replace: true });
      } catch (error) {
        const detail = error?.response?.data?.detail;
        const msg = (typeof detail === 'string' && detail) || 'Authentication failed — please try again.';
        failToHome(msg);
      }
    };

    if (isGoogleFlow) {
      processGoogle();
    } else {
      processEmergent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="theme-fixed min-h-screen flex items-center justify-center bg-[#1C1917]">
      <div className="text-center" data-testid="auth-callback-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
        <p className="text-[#C4B8AE]" style={{fontFamily: 'Work Sans, sans-serif'}}>Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
